import '../../assets/Css/calendar-styles.css';
import React from 'react';

import { NavigateBefore, NavigateNext } from '@material-ui/icons';
import dayjs from 'dayjs';
import ReactCalendar from 'react-calendar';

import { useI18n } from 'Hooks';

import { CalendarButton, CalendarButtons, CalendarWrapper } from './styles';
import {
  getLastMonth,
  getLastWeek,
  getLastYear,
  getThisYear,
  getToday,
  getYesterday,
} from '../../utils/time';

export interface Props {
  selectedDates: Date[] | null;
  setSelectedDates: (date: Date[]) => void;
  minDate?: Date;
  maxDate?: Date;
  range?: boolean;
  buttons?: string[];
  timezone?: string;
}

export const Calendar = ({
  selectedDates,
  setSelectedDates,
  buttons,
  minDate,
  maxDate,
  range = false,
  timezone,
}: Props) => {
  const { t } = useI18n();
  const { startToday, endToday } = getToday(timezone);
  const { startYesterday, endYesterday } = getYesterday(timezone);
  const { startLastWeek, endLastWeek } = getLastWeek(timezone);
  const { startLastMonth, endLastMonth } = getLastMonth(timezone);
  const { startLastYear, endLastYear } = getLastYear(timezone);
  const { startThisYear, endThisYear } = getThisYear(timezone);

  const checkIfActive = (firstDate, secondDate) => {
    if (!selectedDates) return false;
    return (
      dayjs(selectedDates[0]).isSame(firstDate, 'date') &&
      dayjs(selectedDates[1]).isSame(secondDate, 'date')
    );
  };

  const data = {
    today: {
      text: t('General.Today'),
      dates: [startToday, endToday],
      active: selectedDates !== null && checkIfActive(startToday, endToday),
    },

    yesterday: {
      text: t('General.Yesterday'),
      dates: [startYesterday, endYesterday],
      active: selectedDates !== null && checkIfActive(startYesterday, endYesterday),
    },

    lastWeek: {
      text: t('General.LastWeek'),
      dates: [startLastWeek, endLastWeek],
      active: selectedDates !== null && checkIfActive(startLastWeek, endLastWeek),
    },

    lastMonth: {
      text: t('General.LastMonth'),
      dates: [startLastMonth, endLastMonth],
      active: selectedDates !== null && checkIfActive(startLastMonth, endLastMonth),
    },
    thisYear: {
      text: t('General.ThisYear'),
      dates: [startThisYear, endThisYear],
      active: selectedDates !== null && checkIfActive(startThisYear, endThisYear),
    },
    lastYear: {
      text: t('General.LastYear'),
      dates: [startLastYear, endLastYear],
      active: selectedDates !== null && checkIfActive(startLastYear, endLastYear),
    },
  };

  return (
    <CalendarWrapper>
      <ReactCalendar
        onChange={setSelectedDates}
        value={selectedDates ?? null}
        locale={'en'}
        maxDate={maxDate}
        minDate={minDate}
        selectRange={range}
        nextLabel={<NavigateNext />}
        prevLabel={<NavigateBefore />}
        onClickDay={(value) => {
          if (range) {
            setSelectedDates([value, undefined]);
          }
        }}
      />
      <CalendarButtons>
        <>
          {buttons?.map((button, index) => (
            <CalendarButton
              active={data[button].active}
              onClick={() => setSelectedDates(data[button].dates)}
              key={index}>
              {data[button].text}
            </CalendarButton>
          ))}
        </>
      </CalendarButtons>
    </CalendarWrapper>
  );
};
